<script lang="ts" setup>
  import type { ICloseArgs } from '#shared/types/modal.types';
  import type { IPayPalInvoiceData, IRequestInvoiceData, TDepositMethod } from '~/store/cashier';
  import { LazyModalKYC } from '#components';
  import { useAppToast } from '~/composables/shared/useAppToast';
  import { EDepositMethod, useCashierStore } from '~/store/cashier';
  import debounce from '~/utils/debounce';
  import { preventNegative } from '~/utils/inputValidation';

  declare global {
    interface Window {
      paypal?: {
        Buttons: any;
      };
    }
  }

  interface IFormData {
    usdAmount: number | null;
    promoCode?: string;
  }

  const emit = defineEmits<{
    changeMethod: [TDepositMethod];
    close: [ICloseArgs];
  }>();

  const { sendConversionFirstDeposit } = useGtag();
  const cashierStore = useCashierStore();

  const useKYCChecks = !useRuntimeConfig().public.cashierKYCWithdrawalOnly;
  const { showErrorToast, showSuccessToast } = useAppToast();

  const state = reactive<IFormData>({
    usdAmount: null,
    promoCode: '',
  });
  const amountErrorText = ref<string | undefined>();
  const showPayPalModal = ref<boolean>(false);
  const payPalContainer = ref<HTMLElement | null>(null);
  const promoCodeErrorText = ref<string | false>(false);
  const promoIsPending = ref<boolean>(false);

  const DEFAULT_ERROR_TEXT = 'Please enter a valid amount';
  const presetValues = useKYCChecks ? [5, 10, 50, 250, 500] : [10, 20, 50, 250, 500];
  const minValue = presetValues[0] || 0;

  onMounted(() => {
    state.usdAmount = minValue;
  });

  function depositWithCard() {
    if (useKYCChecks && !stores.user.isKycApproved) {
      stores.modals.open('kyc', LazyModalKYC);
      return;
    }

    if (!state.usdAmount) {
      setAmountError();
      return;
    }

    purchaseWithPayPal();
  }

  async function checkAmountChange(event: InputEvent | number) {
    if (!event) return;

    let value = 0;
    let isInvalidValue = false;

    if (typeof event === 'number') {
      value = event;
      isInvalidValue = false;
    } else if (event.target) {
      event.preventDefault();

      const target = event.target as HTMLInputElement;

      value = Number.parseInt(target.value, 10);
      isInvalidValue = Number.isNaN(target.value) || value <= 0 || value > Number.MAX_SAFE_INTEGER;
    }

    if (isInvalidValue) {
      setAmountError();
    } else if (value < minValue) {
      setAmountError(`The minimum acceptable value is $${minValue}`);
    } else {
      state.usdAmount = value;
      resetAmountError();
    }
  }

  const debounceAmountInput = debounce(checkAmountChange, 250);

  async function setAmountError(value = DEFAULT_ERROR_TEXT) {
    state.usdAmount = null;
    await nextTick();
    amountErrorText.value = value;
  }

  function resetAmountError() {
    amountErrorText.value = undefined;
  }

  async function ensurePayPalSdkLoaded(sdkUrlWithParams: string): Promise<void> {
    if (window.paypal) return;

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = sdkUrlWithParams;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load PayPal SDK'));
      document.body.appendChild(script);
    });
  }

  async function purchaseWithPayPal() {
    if (!state.usdAmount) return;

    setPayPalModal(true);

    let success = false;

    try {
      const invoiceRequestData: IRequestInvoiceData = {
        amount: state.usdAmount,
        currency: 'USD',
        method: 'paypal',
      };

      const invoice = (await cashierStore.requestInvoice(invoiceRequestData)) as IPayPalInvoiceData;

      if (!invoice || !payPalContainer.value) {
        showErrorToast();
        return;
      }

      await clearPayPalContainer();

      const { paypalUrl } = useRuntimeConfig().public;
      await ensurePayPalSdkLoaded(`${paypalUrl}&currency=USD`);

      showPayPalButtons(invoice);

      success = true;
    } catch (error: unknown) {
      console.error(error);
      showErrorToast();
    } finally {
      setPayPalModal(success);
    }
  }

  async function clearPayPalContainer() {
    await nextTick();

    if (payPalContainer.value) {
      payPalContainer.value.innerHTML = '';
    }
  }

  function setPayPalModal(value: boolean) {
    showPayPalModal.value = value;
  }

  function closePayPalModal() {
    setPayPalModal(false);
    clearPayPalContainer();
  }

  function showPayPalButtons(payload: IPayPalInvoiceData) {
    if (!window.paypal) return;

    if (!payPalContainer.value) {
      console.error('#paypal-modal-button-container not found');
      return;
    }

    const orderId = payload.order_id;

    window.paypal
      .Buttons({
        createOrder: (data: any, actions: any) =>
          // https://developer.paypal.com/docs/api/orders/v2/#orders_create
          actions.order.create({
            purchase_units: [
              {
                amount: { value: state.usdAmount },
                custom_id: orderId,
              },
            ],
          }),
        onApprove: (data: any, actions: any) =>
          // https://developer.paypal.com/docs/api/orders/v2/#orders_capture
          actions.order.capture().then((details: { id: string; payer: { name: { given_name: string } } }) => {
            sendConversionFirstDeposit({
              value: state.usdAmount || 0,
              currency: 'USD',
              transaction_id: `${orderId}`,
            });

            showSuccessToast('Success', `Transaction completed by ${details?.payer?.name?.given_name}`);

            cashierStore.savePayPalOrderData({ orderId: details?.id, foreignOrderId: orderId });

            closePayPalModal();
          }),
        onError: (error: Error) => {
          console.error('PayPal Payment Error:', error);
          showErrorToast();
        },
      })
      .render('#paypal-modal-button-container');
  }

  async function checkPromoCode() {
    promoIsPending.value = true;
    await delay(250);
    promoCodeErrorText.value = 'This promo code is invalid.';
    await delay(450);
    promoCodeErrorText.value = false;
    promoIsPending.value = false;
  }
</script>

<template>
  <UForm :state="state" class="w-full flex flex-col gap-4 pt-4 pb-6 px-5">
    <button class="opacity-0 w-0 h-0 absolute" autofocus aria-hidden="true" />

    <UFormField
      :error="amountErrorText"
      :ui="{
        label: 'hidden',
        error: 'hidden',
      }"
      name="usdAmount"
      required
    >
      <UInput
        v-model.number="state.usdAmount"
        :placeholder="amountErrorText || 'Enter Amount in USD'"
        :ui="{
          base: 'h-11 pl-8 pr-4 py-3 text-[#101317] font-barlow text-sm font-medium leading-[120%] placeholder:text-[#101317] rounded-[0.375rem]',
          leading: 'pointer-events-none pl-5 pt-[0.1875rem]',
        }"
        variant="subtle"
        class="form-input"
        type="number"
        @input="debounceAmountInput"
        @keydown="preventNegative"
      >
        <template #leading>
          <span class="text-[#101317] font-barlow text-sm font-medium leading-[120%]">$</span>
        </template>
      </UInput>
    </UFormField>

    <div class="w-full items-center justify-between gap-1.5 grid grid-cols-[repeat(auto-fit,minmax(3rem,1fr))]">
      <UButton
        v-for="value in presetValues"
        :key="value"
        class="basis-full font-barlow h-9 px-0"
        color="white"
        @click="checkAmountChange(value)"
      >
        <span class="text-[#101317] text-center font-barlow text-xs xxs:text-sm font-semibold leading-[120%]">
          ${{ value }}
        </span>
      </UButton>
    </div>

    <UFormField
      :ui="{
        label: 'hidden',
        error: promoCodeErrorText
          ? 'shake-animation -translate-y-[2.625rem] bg-white w-auto h-10 mx-[5.625rem] -mb-10 mt-0 ml-4 mr-[5rem] text-center flex items-center text-[#101317] text-[0.875rem] font-medium leading-none'
          : 'hidden',
      }"
      name="usdAmount"
      required
      :error="promoCodeErrorText"
    >
      <UInput
        v-model="state.promoCode"
        :ui="{
          base: 'h-11 px-4 py-3 text-[#101317] font-barlow text-sm font-medium leading-[120%] placeholder:text-[#101317] rounded-[0.375rem]',
          leading: 'pointer-events-none pl-5 pt-[0.1875rem]',
          trailing: 'p-0',
        }"
        variant="subtle"
        placeholder="Promo Code (Optional)"
      >
        <template #trailing>
          <div
            class="flex items-center justify-center w-[4.125rem] h-full px-[1.625rem] py-4 rounded-r-md bg-[#0CBC47]"
            :class="{
              'cursor-not-allowed pointer-events-none opacity-80': promoIsPending,
              'cursor-pointer': !promoIsPending,
            }"
            @click="checkPromoCode"
          >
            <UIcon class="shrink-0 size-6 text-white" name="i-lucide-check" />
          </div>
        </template>
      </UInput>
      <template #error="{ error }">
        {{ error }}
      </template>
    </UFormField>

    <UButton :ui="{ base: 'justify-between h-[2.625rem]' }" @click="depositWithCard">
      <template #leading>
        <SvgoCashierCardOptions class="shrink-0 w-[8.5rem] h-[1.125rem]" filled />
      </template>
      <div class="flex items-center justify-center gap-1">
        <span class="text-white font-barlow text-[0.9375rem] font-semibold leading-[100%] shrink-0">
          Deposit with Card
        </span>
        <SvgoArrowRight class="w-[1.125rem] h-[1.125rem] mt-0.5 shrink-0" />
      </div>
    </UButton>

    <div v-if="useKYCChecks" class="flex items-center justify-center gap-[1.3125rem] self-stretch py-1.5">
      <div class="w-[min(25%,5.125rem)] h-[0.0625rem] opacity-30 bg-white" />
      <div class="shrink-0 text-white text-center font-barlow text-sm font-medium leading-[120%] opacity-60">
        Or proceed without KYC
      </div>
      <div class="w-[min(25%,5.125rem)] h-[0.0625rem] opacity-30 bg-white" />
    </div>

    <UButton :ui="{ base: 'justify-between h-[2.625rem]' }" @click="emit('changeMethod', EDepositMethod.Crypto)">
      <template #leading>
        <NuxtImg
          src="/images/crypto/crypto-options.png"
          format="webp"
          width="112"
          height="22"
          alt="Crypto Options: Bitcoin, USDT, USDC, Ethereum and more"
        />
      </template>
      <div class="flex items-center justify-center gap-1">
        <span class="text-white font-barlow text-[0.9375rem] font-semibold leading-[100%]">Deposit with Crypto</span>
        <SvgoArrowRight class="w-[1.125rem] h-[1.125rem] mt-0.5" />
      </div>
    </UButton>

    <CashierSupportSection @close="emit('close', {})" />

    <!-- Additional modal for PayPal -->
    <div
      v-if="showPayPalModal"
      class="fixed top-0 left-0 w-full h-full bg-black/60 z-[9999] flex justify-center items-center"
    >
      <div
        class="relative w-[28.75rem] max-h-[95%] overflow-y-scroll px-[3.75rem] pt-[3.75rem] pb-[2.5rem] rounded-lg bg-white shadow-[0_2px_10px_rgba(0,0,0,0.2)]"
      >
        <div id="paypal-modal-button-container" ref="payPalContainer" class="w-full text-center">
          <CoreLoadingSpinner />
        </div>
        <UIcon
          class="absolute top-3 right-3 cursor-pointer size-10"
          name="i-ion-close-circle-outline"
          @click="closePayPalModal"
        />
      </div>
    </div>
  </UForm>
</template>
